// src/components/FolderContentArea/FolderContentArea.jsx
import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import axios from 'axios';
import { FlexGroup, FlexItem, Icon, SegmentControl, Table, Text, useTable } from '@zenqms/zui';
import { handleGenericError } from '../../handle-generic-error';
import { displayToast } from '../../app/app-slice';
import { sessionExpiredDetected } from '../../auth/auth-slice';
import {
  buildAggregatedColumns,
  buildAggregatedFilterColumns,
  buildDocumentColumns,
  buildDocumentFilterColumns,
  buildWorkflowColumns,
  buildWorkflowFilterColumns,
  buildTaskColumns,
  buildTaskFilterColumns,
  resolveModuleLabel,
  toZuiIconName,
  DOCUMENT_HIDDEN_COLUMNS,
  WORKFLOW_HIDDEN_COLUMNS,
  TASK_HIDDEN_COLUMNS,
} from './folderContentAreaColumns';
import { DocumentPreviewPanel } from './DocumentPreviewPanel';
import './FolderContentArea.scss';

const ALL_TYPES_VALUE = '__all_types__';
const DOCUMENTS_VALUE = 'document';
const TASKS_VALUE = 'task';

export const WORKFLOW_REQUEST_COLUMNS = [
  'key_id',
  'name',
  'current_stage',
  'stage_assignees',
  'stage_due_date',
  'project_assignees',
  'status',
  'item_id',
  'audit_module',
];

// ─── API ──────────────────────────────────────────────────────────────────────

async function searchItems(url, shareId, folderId, body) {
  const r = await axios.post(`${url}/share/folders/${shareId}/folders/${folderId}/items`, body);
  return r.data;
}

async function searchDocuments(url, shareId, folderId, body) {
  const r = await axios.post(`${url}/share/folders/${shareId}/folders/${folderId}/documents`, body);
  return r.data;
}

async function searchWorkflows(url, shareId, folderId, body) {
  const r = await axios.post(`${url}/share/folders/${shareId}/folders/${folderId}/workflows`, body);
  return r.data;
}

async function searchTasks(url, shareId, folderId, body) {
  const r = await axios.post(`${url}/share/folders/${shareId}/folders/${folderId}/tasks`, body);
  return r.data;
}

async function fetchDocumentPdfUrl(url, shareId, folderId, documentVersionId) {
  const r = await axios.get(
    `${url}/share/folders/${shareId}/folders/${folderId}/documents/${documentVersionId}/pdf-url`
  );
  return r.data;
}

async function fetchWorkflowPdfUrl(url, shareId, folderId, workflowId) {
  const r = await axios.get(`${url}/share/folders/${shareId}/folders/${folderId}/workflows/${workflowId}/pdf-url`);
  return r.data;
}

async function fetchTaskPdfUrl(url, shareId, folderId, taskId) {
  const r = await axios.get(`${url}/share/folders/${shareId}/folders/${folderId}/tasks/${taskId}/pdf-url`);
  return r.data;
}

async function checkFolderExists(url, shareId, folderId) {
  try {
    await axios.get(`${url}/share/folders/${shareId}/folders/${folderId}`);
    return true;
  } catch (err) {
    const status = err?.response?.status;
    return !(status === 404 || status === 410 || status === 403);
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Extracts the lowercase file extension from a pre-signed S3 URL.
 * Pre-signed URLs carry query parameters after a `?`; we only inspect the
 * path portion so the signature tokens don't pollute the extension match.
 *
 * Returns e.g. 'pdf', 'mp4', 'docx', or null if no extension is found.
 */
function extractFileExtension(url) {
  if (!url) return null;
  try {
    const pathname = new URL(url).pathname;
    const lastSegment = pathname.split('/').pop() ?? '';
    const dotIndex = lastSegment.lastIndexOf('.');
    if (dotIndex === -1) return null;
    return lastSegment.slice(dotIndex + 1).toLowerCase();
  } catch {
    return null;
  }
}

// ─── EmptyFolderState ─────────────────────────────────────────────────────────

function EmptyFolderState({ folderName }) {
  const { formatMessage } = useIntl();
  return (
    <div className="folder-content-area__empty-state">
      <div className="folder-content-area__empty-icon-wrapper">
        <Icon name="folder" size="lg" noHover className="folder-content-area__empty-icon" />
      </div>
      <p className="folder-content-area__empty-title">
        {formatMessage({ id: 'folderContentArea.emptyState.title', defaultMessage: 'This folder is empty' })}
      </p>
      <p className="folder-content-area__empty-subtitle">
        {formatMessage(
          {
            id: 'folderContentArea.emptyState.subtitle',
            defaultMessage: '"{folderName}" does not contain any items yet.',
          },
          { folderName }
        )}
      </p>
    </div>
  );
}

// ─── FolderDataTable ──────────────────────────────────────────────────────────

/**
 * Single-view data table.
 *
 * For the aggregated ALL_TYPES_VALUE view: calls /items, extracts
 * facets.types from the response and passes them up via onAggregatedFacets.
 *
 * For DOCUMENTS_VALUE: calls /documents.
 *
 * For any other value (a workflow audit_module string): calls /items with
 * an auditModuleFilter pre-applied in the request body.
 */
function FolderDataTable({
  folderShareId,
  folderId,
  view,
  auditModuleFilter,
  onEmptyChange,
  onOpenDocument,
  onAggregatedFacets,
  aggregatedFacets = [],
  allowDownload = false,
  enabled = true,
  refreshKey = 0,
  onFolderGone,
}) {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const zensignApiUrl = window.config.zensignApiUrl;

  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  // Derive a single viewType instead of four boolean flags.
  const viewType =
    view === ALL_TYPES_VALUE
      ? 'aggregated'
      : view === DOCUMENTS_VALUE
      ? 'document'
      : view === TASKS_VALUE
      ? 'task'
      : 'workflow';

  const onAggregatedFacetsRef = useRef(onAggregatedFacets);
  onAggregatedFacetsRef.current = onAggregatedFacets;

  const columns = useMemo(() => {
    const downloadContext = allowDownload ? { folderShareId, folderId } : null;
    if (viewType === 'document') return buildDocumentColumns(formatMessage, onOpenDocument, downloadContext);
    if (viewType === 'task') return buildTaskColumns(formatMessage, onOpenDocument, downloadContext);
    if (viewType === 'workflow') return buildWorkflowColumns(formatMessage, onOpenDocument, downloadContext);
    return buildAggregatedColumns(formatMessage, aggregatedFacets, onOpenDocument, downloadContext);
  }, [viewType, onOpenDocument, formatMessage, aggregatedFacets, allowDownload, folderShareId, folderId]);

  const filterColumns = useMemo(() => {
    if (viewType === 'document') return buildDocumentFilterColumns(formatMessage);
    if (viewType === 'task') return buildTaskFilterColumns(formatMessage);
    if (viewType === 'workflow') return buildWorkflowFilterColumns(formatMessage);
    return buildAggregatedFilterColumns(formatMessage, aggregatedFacets);
  }, [viewType, formatMessage, aggregatedFacets]);

  const hiddenColumns =
    viewType === 'document'
      ? DOCUMENT_HIDDEN_COLUMNS
      : viewType === 'workflow'
      ? WORKFLOW_HIDDEN_COLUMNS
      : viewType === 'task'
      ? TASK_HIDDEN_COLUMNS
      : {};

  const table = useTable({
    initialPageSize: 10,
    pageSizeOptions: [5, 10, 25],
    filters: { columns: filterColumns },
    ...(Object.keys(hiddenColumns).length > 0 && { initialColumnVisibility: hiddenColumns }),
  });

  const { stateForApi, filters, setTotalRecords: setTableTotal } = table;

  const onEmptyChangeRef = useRef(onEmptyChange);
  onEmptyChangeRef.current = onEmptyChange;

  const onFolderGoneRef = useRef(onFolderGone);
  onFolderGoneRef.current = onFolderGone;

  const fetchData = useCallback(async () => {
    if (!folderShareId || !folderId || !enabled) return;
    setIsLoading(true);
    try {
      // Build filter list: for workflow tabs prepend the audit_module filter (mirrors FoldersTable.tsx)
      const baseFilters = filters.filtersForApi ?? [];
      const requestFilters =
        auditModuleFilter && viewType === 'workflow'
          ? [{ columnName: 'audit_module', value: auditModuleFilter, operation: 'equal' }, ...baseFilters]
          : baseFilters;

      const body = {
        query: {
          current_page: stateForApi.pageIndex,
          page_size: stateForApi.pageSize,
          ...(stateForApi.sorting?.[0] && {
            sort_by: stateForApi.sorting[0].id,
            order_by: stateForApi.sorting[0].desc ? 'desc' : 'asc',
          }),
        },
        filters: requestFilters,
        ...(viewType === 'workflow' && { columns: WORKFLOW_REQUEST_COLUMNS }),
      };

      const result =
        viewType === 'document'
          ? await searchDocuments(zensignApiUrl, folderShareId, folderId, body)
          : viewType === 'task'
          ? await searchTasks(zensignApiUrl, folderShareId, folderId, body)
          : viewType === 'workflow'
          ? await searchWorkflows(zensignApiUrl, folderShareId, folderId, body)
          : await searchItems(zensignApiUrl, folderShareId, folderId, body);

      const rows = result.data ?? [];
      const total = result.pagination?.total_records ?? rows.length;
      setData(rows);
      setTotalRecords(total);
      setTableTotal(total);

      if (viewType === 'aggregated') {
        const apiFacets = result.facets?.types ?? [];
        // If the API returned document rows but no document facet (backend limitation:
        // folders_workflow_types_view doesn't include a document row), synthesise one.
        const hasDocumentFacet = apiFacets.some((f) => f.audit_module === 'document');
        const hasDocumentRows = (result.data ?? []).some((r) => !r.audit_module || r.audit_module === 'document');
        const baseFacets =
          !hasDocumentFacet && hasDocumentRows
            ? [{ audit_module: 'document', plural_name: 'Documents', icon: 'file', count: 0 }, ...apiFacets]
            : apiFacets;

        // Back-fill missing facet icons from per-row item_type_icon data.
        // The API sometimes returns facets with an empty icon field; the
        // rows themselves always carry item_type_icon from the DB view.
        const rowIconByModule = {};
        (result.data ?? []).forEach((r) => {
          const mod = r.audit_module?.toLowerCase() || 'document';
          if (r.item_type_icon && !rowIconByModule[mod]) rowIconByModule[mod] = r.item_type_icon;
        });
        const facets = baseFacets.map((f) => (f.icon ? f : { ...f, icon: rowIconByModule[f.audit_module] ?? '' }));

        if (facets.length > 0) onAggregatedFacetsRef.current(facets);
        onEmptyChangeRef.current(total === 0 && !filters.hasActiveFilters);
      }
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        dispatch(sessionExpiredDetected());
        return;
      } else if (status === 403) {
        dispatch(displayToast(formatMessage({ id: 'errors.itemNotFound' })));
        onFolderGoneRef.current?.();
      } else if (status === 404 || status === 410) {
        dispatch(displayToast(formatMessage({ id: 'errors.itemNotFound' })));
        onFolderGoneRef.current?.();
      } else {
        handleGenericError(dispatch, err, navigate);
        dispatch(displayToast(formatMessage({ id: 'errors.somethingWentWrong' })));
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    folderShareId,
    folderId,
    viewType,
    auditModuleFilter,
    enabled,
    refreshKey,
    stateForApi,
    filters.filtersForApi,
    filters.hasActiveFilters,
    zensignApiUrl,
    dispatch,
    navigate,
    setTableTotal,
    formatMessage,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    table.setVisibleRowIds(
      data.map((row) => row.document_version_id ?? row.item_id ?? row.task_id ?? row.id).filter(Boolean)
    );
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const emptyState = (
    <FlexGroup direction="column" alignItems="center" gap="md" style={{ padding: '40px 0' }}>
      <Icon name="folder-open" size="xl" noHover color="var(--zui-color-gray-400)" />
      <Text size="md">
        {formatMessage({ id: 'folderContentArea.empty', defaultMessage: 'No items match the current filters.' })}
      </Text>
    </FlexGroup>
  );

  return (
    <Table
      {...table.tableProps}
      filterColumns={filterColumns}
      columns={columns}
      data={data}
      totalRecords={totalRecords}
      isLoading={isLoading}
      getRowId={(row) => row.document_version_id ?? row.item_id ?? row.task_id ?? row.id ?? row.key_id ?? row.name}
      enableColumnDnD={false}
    >
      <Table.Toolbar>
        <Table.Toolbar.Right>
          <Table.Toolbar.ManageColumns />
        </Table.Toolbar.Right>
      </Table.Toolbar>
      <Table.Content emptyState={emptyState} />
      <Table.Footer />
    </Table>
  );
}

// ─── FolderContentArea ────────────────────────────────────────────────────────

export function FolderContentArea({ folderShareId, folderId, folderName, allowDownload, onFolderGone }) {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const zensignApiUrl = window.config.zensignApiUrl;

  // selectedSegment is either ALL_TYPES_VALUE, DOCUMENTS_VALUE, or an audit_module string
  const [selectedSegment, setSelectedSegment] = useState(ALL_TYPES_VALUE);
  // facets lifted from the aggregated fetch — drives the segment items dynamically
  const [facets, setFacets] = useState([]);
  const [isFolderEmpty, setIsFolderEmpty] = useState(false);
  const [resolvedFolderId, setResolvedFolderId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // ── Document preview panel state ──────────────────────────────────────────
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewName, setPreviewName] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewFileExtension, setPreviewFileExtension] = useState(null);

  // Reset all per-folder state when folderId changes
  useEffect(() => {
    setSelectedSegment(ALL_TYPES_VALUE);
    setFacets([]);
    setIsFolderEmpty(false);
    setResolvedFolderId(null);
    setPreviewOpen(false);
    setPreviewFileExtension(null);
  }, [folderId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEmptyChange = useCallback(
    (isEmpty) => {
      setIsFolderEmpty(isEmpty);
      setResolvedFolderId(folderId);
    },
    [folderId]
  );

  const handleOpenDocument = useCallback(
    async (id, documentName, itemType) => {
      setPreviewName(documentName ?? '');
      setPreviewUrl(null);
      setPreviewFileExtension(null);
      setPreviewLoading(true);
      setPreviewOpen(true);
      try {
        const fetcher =
          itemType === 'task' ? fetchTaskPdfUrl : itemType === 'workflow' ? fetchWorkflowPdfUrl : fetchDocumentPdfUrl;
        const { url } = await fetcher(zensignApiUrl, folderShareId, folderId, id);
        setPreviewUrl(url);
        setPreviewFileExtension(extractFileExtension(url));
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401) {
          setPreviewOpen(false);
          dispatch(sessionExpiredDetected());
          return;
        } else if (status === 403) {
          dispatch(
            displayToast(
              formatMessage({
                id: 'errors.itemNoLongerAvailable',
                defaultMessage: 'This item is no longer available.',
              })
            )
          );
          onFolderGone?.();
        } else if (status === 404 || status === 410) {
          const folderStillExists = await checkFolderExists(zensignApiUrl, folderShareId, folderId);
          dispatch(
            displayToast(
              formatMessage({
                id: 'errors.itemNoLongerAvailable',
                defaultMessage: 'This item is no longer available.',
              })
            )
          );
          if (folderStillExists) {
            setRefreshKey((key) => key + 1);
          } else {
            onFolderGone?.();
          }
        } else {
          handleGenericError(dispatch, err, navigate);
          dispatch(
            displayToast(
              formatMessage({
                id: 'errors.somethingWentWrong',
                defaultMessage: 'Something went wrong. Please try again.',
              })
            )
          );
        }
        setPreviewOpen(false);
      } finally {
        setPreviewLoading(false);
      }
    },
    [zensignApiUrl, folderShareId, folderId, onFolderGone, dispatch, navigate, formatMessage]
  );

  // Sort facets: Documents first, then alphabetical by plural_name.
  // Mirrors the UI workspace sort in FoldersContentArea.tsx.
  const sortedFacets = useMemo(() => {
    return [...facets].sort((a, b) => {
      if (a.audit_module === DOCUMENTS_VALUE) return -1;
      if (b.audit_module === DOCUMENTS_VALUE) return 1;
      return a.plural_name.localeCompare(b.plural_name, undefined, { sensitivity: 'base' });
    });
  }, [facets]);

  // Build segment items dynamically: "All types" + one per facet from the API.
  // - Documents facet → hits /documents endpoint (same as before)
  // - Any other facet → hits /items endpoint with audit_module filter pre-applied
  const segmentItems = useMemo(() => {
    const items = [
      {
        value: ALL_TYPES_VALUE,
        label: formatMessage({ id: 'folderContentArea.segment.allTypes', defaultMessage: 'All types' }),
        icon: 'grid',
      },
    ];
    sortedFacets.forEach((facet) => {
      const isDoc = facet.audit_module === DOCUMENTS_VALUE;
      const icon = isDoc ? 'file' : toZuiIconName(facet.icon) ?? 'diagram-project';
      items.push({
        value: facet.audit_module,
        label: resolveModuleLabel(facet.audit_module, sortedFacets),
        icon,
      });
    });
    return items;
  }, [sortedFacets, formatMessage]);

  if (!folderShareId || !folderId) {
    return (
      <FlexGroup
        direction="column"
        alignItems="center"
        justifyContent="center"
        className="folder-content-area folder-content-area--no-selection"
      >
        <Icon name="folder" size="xl" noHover color="var(--zui-color-gray-300)" />
        <Text size="md">
          {formatMessage({
            id: 'folderContentArea.noFolderSelected',
            defaultMessage: 'Select a folder to view its contents.',
          })}
        </Text>
      </FlexGroup>
    );
  }

  const isFolderLoading = resolvedFolderId !== folderId;
  const showEmptyState = !isFolderLoading && isFolderEmpty;

  // Derive a single segment type to drive rendering.
  const segmentType =
    selectedSegment === ALL_TYPES_VALUE
      ? 'aggregated'
      : selectedSegment === DOCUMENTS_VALUE
      ? 'document'
      : selectedSegment === TASKS_VALUE
      ? 'task'
      : 'workflow';
  const workflowAuditModule = segmentType === 'workflow' ? selectedSegment : null;

  return (
    <FlexGroup direction="column" className="folder-content-area folder-content-area--with-preview">
      <FlexItem grow={1} className="folder-content-area__main" style={{ minWidth: 0 }}>
        <FlexGroup direction="column" style={{ height: '100%' }}>
          {!showEmptyState && (
            <FlexItem className="folder-content-area__switcher">
              <SegmentControl
                aria-label={formatMessage({
                  id: 'folderContentArea.segment.ariaLabel',
                  defaultMessage: 'View switcher',
                })}
                value={selectedSegment}
                onValueChange={setSelectedSegment}
                items={segmentItems}
                size="md"
                maxWidth="100%"
              />
            </FlexItem>
          )}

          <FlexItem grow={1} className="folder-content-area__table-container">
            {showEmptyState && <EmptyFolderState folderName={folderName ?? ''} />}

            <div style={{ display: showEmptyState ? 'none' : undefined }}>
              {/* Aggregated (All types) — always mounted; feeds facets to segmentItems */}
              <div className={segmentType !== 'aggregated' ? 'folder-content-area__table--hidden' : undefined}>
                <FolderDataTable
                  key={`all-${folderShareId}-${folderId}`}
                  refreshKey={refreshKey}
                  folderShareId={folderShareId}
                  folderId={folderId}
                  view={ALL_TYPES_VALUE}
                  auditModuleFilter={null}
                  onEmptyChange={handleEmptyChange}
                  onOpenDocument={handleOpenDocument}
                  onAggregatedFacets={setFacets}
                  aggregatedFacets={sortedFacets}
                  allowDownload={allowDownload}
                  onFolderGone={onFolderGone}
                />
              </div>

              {/* Documents tab */}
              <div className={segmentType !== 'document' ? 'folder-content-area__table--hidden' : undefined}>
                <FolderDataTable
                  key={`docs-${folderShareId}-${folderId}`}
                  refreshKey={refreshKey}
                  folderShareId={folderShareId}
                  folderId={folderId}
                  view={DOCUMENTS_VALUE}
                  auditModuleFilter={null}
                  onEmptyChange={() => {}}
                  onOpenDocument={handleOpenDocument}
                  onAggregatedFacets={() => {}}
                  allowDownload={allowDownload}
                  enabled={segmentType === 'document'}
                  onFolderGone={onFolderGone}
                />
              </div>

              {/* Workflow type tab — one shared table, re-keyed on the selected audit_module
                  so each type gets its own pagination/filter state */}
              {segmentType === 'workflow' && (
                <FolderDataTable
                  key={`wf-${folderShareId}-${folderId}-${workflowAuditModule}`}
                  refreshKey={refreshKey}
                  folderShareId={folderShareId}
                  folderId={folderId}
                  view={workflowAuditModule}
                  auditModuleFilter={workflowAuditModule}
                  onEmptyChange={() => {}}
                  onOpenDocument={handleOpenDocument}
                  onAggregatedFacets={() => {}}
                  allowDownload={allowDownload}
                  onFolderGone={onFolderGone}
                />
              )}

              {/* Tasks tab */}
              {segmentType === 'task' && (
                <FolderDataTable
                  key={`tasks-${folderShareId}-${folderId}`}
                  refreshKey={refreshKey}
                  folderShareId={folderShareId}
                  folderId={folderId}
                  view={TASKS_VALUE}
                  auditModuleFilter={null}
                  onEmptyChange={() => {}}
                  onOpenDocument={handleOpenDocument}
                  onAggregatedFacets={() => {}}
                  allowDownload={allowDownload}
                  enabled={segmentType === 'task'}
                  onFolderGone={onFolderGone}
                />
              )}
            </div>
          </FlexItem>
        </FlexGroup>
      </FlexItem>

      <DocumentPreviewPanel
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewFileExtension(null);
        }}
        documentName={previewName}
        url={previewUrl}
        isLoading={previewLoading}
        fileExtension={previewFileExtension}
      />
    </FlexGroup>
  );
}
